"use client";

import { createContext, useContext, useEffect, useState } from "react";
import { Socket } from "socket.io-client";
import { connectSocket, disconnectSocket } from "@/lib/socket";
import useSession from "@/hooks/QueryHooks/useSession";
import { WEBSOCKET_EVENTS } from "@/constants/websocket";
import { useQueryClient } from "@tanstack/react-query";
import { ChatConversationSchemaType, ConversationCreatedSchemaType, MessageDeletedSchemaType, MessageSchemaType, MessagesReadSchemaType } from "../../modules/chat/api/schemas";
import { USER_CONVERSATIONS_QUERY_KEYS } from "../../modules/chat/api/queries/useUserConversations";
import { Query } from "@/types/queryTpes";
import { USER_CONVERSATION_BYID__QUERY_KEYS } from "../../modules/chat/api/queries/useConversationById";
import { devLog } from "@/lib/devLog";
import { playNotificationSound } from "@/lib/notificationSound";
type SocketContextType = {
  socket: Socket | null;
  isConnected: boolean;
  reconnectSocket: () => void;
  ensureConnected: () => void;
};
const SocketContext = createContext<SocketContextType>({
  socket: null,
  isConnected: false,
  reconnectSocket: () => {},
  ensureConnected: () => {}
});
export const useSocketContext = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error("useSocketContext must be used within SocketProvider");
  }
  return context;
};
export const SocketProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const {
    data: session
  } = useSession();
  const queryClient = useQueryClient();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const reconnectSocket = () => {
    if (!session?.id) return;
    disconnectSocket();
    const newSocketInstance = connectSocket(session.id);
    setSocket(newSocketInstance);
    setIsConnected(newSocketInstance.connected);
  };
  const ensureConnected = () => {
    if (!session?.id) return;
    const instance = connectSocket(session.id);
    setSocket(instance);
    setIsConnected(instance.connected);
  };
  useEffect(() => {
    if (!session?.id) return;
    const socketInstance = connectSocket(session.id);
    setSocket(socketInstance);
    setIsConnected(socketInstance.connected);
    const onConnect = () => {
      console.log("✅ Socket connected successfully!");
      setIsConnected(true);
    };
    const onDisconnect = () => {
      console.log("❌ Socket disconnected");
      setIsConnected(false);
    };
    const onError = (error: Error) => {
      console.error("🔴 Socket error:", error);
    };
    const onConnectError = (error: Error) => {
      console.error("🔴 Socket connection error:", error);
    };
    socketInstance.on("connect", onConnect);
    socketInstance.on("disconnect", onDisconnect);
    socketInstance.on("error", onError);
    socketInstance.on("connect_error", onConnectError);
    return () => {
      socketInstance.off("connect", onConnect);
      socketInstance.off("disconnect", onDisconnect);
      socketInstance.off("error", onError);
      socketInstance.off("connect_error", onConnectError);
      disconnectSocket();
      setSocket(null);
      setIsConnected(false);
    };
  }, [session?.id]);
  useEffect(() => {
    if (!session?.id) return;
    const revive = () => {
      if (document.visibilityState !== "visible") return;
      if (socket?.connected) return;
      devLog.log("🔄 Socket down on wake-up, reconnecting...");
      ensureConnected();
    };
    document.addEventListener("visibilitychange", revive);
    window.addEventListener("focus", revive);
    window.addEventListener("online", revive);
    return () => {
      document.removeEventListener("visibilitychange", revive);
      window.removeEventListener("focus", revive);
      window.removeEventListener("online", revive);
    };
  }, [session?.id, socket]);
  useEffect(() => {
    if (!socket || !session?.id) return;
    const handleNewMessage = (newMessage: MessageSchemaType) => {
      if (newMessage.senderId !== session.id) {
        playNotificationSound();
      }
      // If the message belongs to a conversation not in the list (e.g. the user
      // deleted/hid it), refetch so the reappeared thread comes back — the
      // backend now returns it since its lastMessageAt exceeds the delete marker.
      const currentConversations = queryClient.getQueryData<Query<ChatConversationSchemaType[]>>(USER_CONVERSATIONS_QUERY_KEYS.getUserConversations(session.id));
      const isKnownConversation = currentConversations?.data?.some(conversation => conversation.id === newMessage.conversationId);
      if (!isKnownConversation) {
        queryClient.invalidateQueries({
          queryKey: USER_CONVERSATIONS_QUERY_KEYS.all
        });
      }
      queryClient.setQueryData(USER_CONVERSATIONS_QUERY_KEYS.getUserConversations(session.id), (oldData: Query<ChatConversationSchemaType[]>) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          data: oldData.data.map(conversation => {
            if (conversation.id === newMessage.conversationId) {
              return {
                ...conversation,
                lastMessage: newMessage,
                lastMessageAt: newMessage.createdAt,
                unreadCount: conversation.unreadCount !== undefined && newMessage.senderId !== session.id ? conversation.unreadCount + 1 : conversation.unreadCount
              };
            }
            return conversation;
          })
        };
      });
      queryClient.setQueryData(USER_CONVERSATION_BYID__QUERY_KEYS.getConversationById(newMessage.conversationId), (oldData: any) => {
        if (!oldData) return oldData;
        const firstPage = oldData.pages?.[0];
        if (!firstPage) return oldData;
        return {
          ...oldData,
          pages: [{
            ...firstPage,
            data: {
              ...firstPage.data,
              conversation: {
                ...firstPage.data.conversation,
                unreadCount: oldData.pages[0].data.conversation.unreadCount !== undefined && newMessage.senderId !== session.id ? firstPage.data.conversation.unreadCount + 1 : firstPage.data.conversation.unreadCount,
                unreadLastMessageAt: newMessage.createdAt
              },
              messages: [newMessage, ...firstPage.data.messages]
            }
          }, ...oldData.pages.slice(1)]
        };
      });
    };
    socket.on(WEBSOCKET_EVENTS.NEW_MESSAGE, handleNewMessage);
    return () => {
      socket.off(WEBSOCKET_EVENTS.NEW_MESSAGE, handleNewMessage);
    };
  }, [socket, session?.id, queryClient]);
  useEffect(() => {
    if (!socket || !session?.id) return;

    // A message the current user just sent. If its conversation isn't in the
    // list (opened via recipientId, or a previously-deleted thread), refetch so
    // it appears on the left — the message is already persisted server-side.
    const handleMessageSent = (message: {
      conversationId?: string;
    }) => {
      if (!message?.conversationId) return;
      const list = queryClient.getQueryData<Query<ChatConversationSchemaType[]>>(USER_CONVERSATIONS_QUERY_KEYS.getUserConversations(session.id));
      const isKnown = list?.data?.some(conversation => conversation.id === message.conversationId);
      if (!isKnown) {
        queryClient.invalidateQueries({
          queryKey: USER_CONVERSATIONS_QUERY_KEYS.all
        });
      }
    };
    socket.on(WEBSOCKET_EVENTS.MESSAGE_SENT, handleMessageSent);
    return () => {
      socket.off(WEBSOCKET_EVENTS.MESSAGE_SENT, handleMessageSent);
    };
  }, [socket, session?.id, queryClient]);
  useEffect(() => {
    if (!socket || !session?.id) return;
    const handleMessageDeleted = ({
      messageId,
      conversationId
    }: MessageDeletedSchemaType) => {
      queryClient.setQueryData(USER_CONVERSATION_BYID__QUERY_KEYS.getConversationById(conversationId), (oldData: any) => {
        if (!oldData?.pages) return oldData;
        return {
          ...oldData,
          pages: oldData.pages.map((page: any) => ({
            ...page,
            data: {
              ...page.data,
              messages: page.data.messages.map((m: MessageSchemaType) => m.id === messageId ? {
                ...m,
                deleted: true,
                content: ""
              } : m)
            }
          }))
        };
      });
      queryClient.setQueryData(USER_CONVERSATIONS_QUERY_KEYS.getUserConversations(session.id), (oldData: Query<ChatConversationSchemaType[]>) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          data: oldData.data.map(conversation => conversation.lastMessage?.id === messageId ? {
            ...conversation,
            lastMessage: {
              ...conversation.lastMessage,
              deleted: true,
              content: ""
            }
          } : conversation)
        };
      });
    };
    socket.on(WEBSOCKET_EVENTS.MESSAGE_DELETED, handleMessageDeleted);
    return () => {
      socket.off(WEBSOCKET_EVENTS.MESSAGE_DELETED, handleMessageDeleted);
    };
  }, [socket, session?.id, queryClient]);
  useEffect(() => {
    if (!socket || !session?.id) return;
    const handleUserOnline = ({
      userId
    }: {
      userId: string;
    }) => {
      queryClient.setQueryData(USER_CONVERSATIONS_QUERY_KEYS.getUserConversations(session.id), (oldData: Query<ChatConversationSchemaType[]>) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          data: oldData.data.map(conversation => {
            if (conversation.otherUser.id === userId) {
              return {
                ...conversation,
                otherUser: {
                  ...conversation.otherUser,
                  isOnline: true
                }
              };
            }
            return conversation;
          })
        };
      });
    };
    const handleUserOffline = ({
      userId
    }: {
      userId: string;
    }) => {
      queryClient.setQueryData(USER_CONVERSATIONS_QUERY_KEYS.getUserConversations(session.id), (oldData: Query<ChatConversationSchemaType[]>) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          data: oldData.data.map(conversation => {
            if (conversation.otherUser.id === userId) {
              return {
                ...conversation,
                otherUser: {
                  ...conversation.otherUser,
                  isOnline: false
                }
              };
            }
            return conversation;
          })
        };
      });
    };
    socket.on(WEBSOCKET_EVENTS.USER_ONLINE, handleUserOnline);
    socket.on(WEBSOCKET_EVENTS.USER_OFFLINE, handleUserOffline);
    return () => {
      socket.off(WEBSOCKET_EVENTS.USER_ONLINE, handleUserOnline);
      socket.off(WEBSOCKET_EVENTS.USER_OFFLINE, handleUserOffline);
    };
  }, [socket, session?.id, queryClient]);
  useEffect(() => {
    if (!socket || !session?.id) return;
    const handleConversationCreated = (data: ConversationCreatedSchemaType) => {
      queryClient.setQueryData(USER_CONVERSATIONS_QUERY_KEYS.getUserConversations(session.id), (oldData: Query<ChatConversationSchemaType[]>) => {
        if (!oldData) return oldData;

        // Convert the conversation to match ChatConversationSchemaType
        const newConversation: ChatConversationSchemaType = {
          id: data.conversation.id,
          user1id: data.conversation.user1Id,
          user2id: data.conversation.user2Id,
          participants: [data.conversation.user1Id, data.conversation.user2Id],
          lastMessageAt: data.conversation.lastMessageAt,
          createdAt: data.conversation.createdAt,
          lastMessage: data.conversation.lastMessage,
          unreadCount: data.conversation.unreadCount,
          otherUser: {
            ...data.conversation.otherUser
          },
          realEstate: data.conversation.realEstate ?? null
        };

        // Add the new conversation at the beginning of the list
        return {
          ...oldData,
          data: [newConversation, ...oldData.data]
        };
      });
    };
    socket.on(WEBSOCKET_EVENTS.CONVERSATION_CREATED, handleConversationCreated);
    return () => {
      socket.off(WEBSOCKET_EVENTS.CONVERSATION_CREATED, handleConversationCreated);
    };
  }, [socket, session?.id, queryClient]);
  useEffect(() => {
    if (!socket || !session?.id) return;
    const handleMessagesRead = (data: MessagesReadSchemaType) => {
      queryClient.setQueryData(USER_CONVERSATION_BYID__QUERY_KEYS.getConversationById(data.conversationId), (oldData: any) => {
        if (!oldData) return oldData;
        const firstPage = oldData.pages?.[0];
        if (!firstPage) return oldData;
        return {
          ...oldData,
          pages: oldData.pages.map((page: any, index: number) => {
            if (index === 0) {
              return {
                ...page,
                data: {
                  ...page.data,
                  conversation: {
                    ...page.data.conversation,
                    otherUserLastReadAt: data.readAt
                  }
                }
              };
            }
            return page;
          })
        };
      });
    };
    socket.on(WEBSOCKET_EVENTS.MESSAGES_READ, handleMessagesRead);
    return () => {
      socket.off(WEBSOCKET_EVENTS.MESSAGES_READ, handleMessagesRead);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, session?.id]);
  return <SocketContext.Provider value={{
    socket,
    isConnected,
    reconnectSocket,
    ensureConnected
  }} data-sentry-element="SocketContext.Provider" data-sentry-component="SocketProvider" data-sentry-source-file="SocketProvider.tsx">
      {children}
    </SocketContext.Provider>;
};